/**
 * Deterministic model for the Ions & Bonding Lab. Every function is pure and fails closed
 * (returns null or an `unsupported` result) for input outside the reviewed data.
 */
import {
  COVALENT_MOLECULE_CASES,
  ELEMENT_FACTS,
  ION_ELEMENT_CASES,
  ION_SPECIES,
  ION_UNSUPPORTED_CASES,
  STRUCTURE_LABELS,
  type CovalentMoleculeCase,
  type ElementSymbol,
  type IonicCompoundCase,
  type IonSpecies,
  type MolecularStructure,
  type ShapeName,
  type StructureSubstance,
  type StructureType,
  type VseprSpeciesCase,
} from './ionsBondingData';

export * from './ionsBondingData';

/* ------------------------------------------------------- typography */

const SUBSCRIPT_DIGITS = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
const SUPERSCRIPT_DIGITS = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];

export function toSubscript(value: number): string {
  return String(value)
    .split('')
    .map((digit) => SUBSCRIPT_DIGITS[Number(digit)])
    .join('');
}

/** Charge as a superscript, e.g. 2 → '²⁺', -1 → '⁻'. */
export function formatCharge(charge: number): string {
  if (charge === 0) return '';
  const magnitude = Math.abs(charge);
  const digits =
    magnitude === 1
      ? ''
      : String(magnitude)
          .split('')
          .map((digit) => SUPERSCRIPT_DIGITS[Number(digit)])
          .join('');
  return `${digits}${charge > 0 ? '⁺' : '⁻'}`;
}

/** Signed charge in plain text, e.g. 2 → '+2', -3 → '−3'. */
export function signedCharge(charge: number): string {
  return charge > 0 ? `+${charge}` : `−${Math.abs(charge)}`;
}

export function ionLabel(ion: IonSpecies): string {
  return `${ion.core}${formatCharge(ion.charge)}`;
}

/* ------------------------------------------------------ shell model */

/** Shell capacities used by the Year 10 model for Z ≤ 20 (2, 8, 8, 2). */
const SHELL_CAPACITIES = [2, 8, 8, 2];
export const SHELL_MODEL_MAX_Z = 20;
const FULL_OUTER_SHELL = 8;
const HYDROGEN_FULL_SHELL = 2;
const MAX_ELECTRONS_LOST = 3;
const MIN_ELECTRONS_GAINED_VALENCE = 5;

export function shellArrangement(atomicNumber: number): number[] | null {
  if (!Number.isInteger(atomicNumber) || atomicNumber < 1 || atomicNumber > SHELL_MODEL_MAX_Z) return null;
  const shells: number[] = [];
  let remaining = atomicNumber;
  for (const capacity of SHELL_CAPACITIES) {
    if (remaining <= 0) break;
    const placed = Math.min(capacity, remaining);
    shells.push(placed);
    remaining -= placed;
  }
  return shells;
}

export function formatShells(shells: number[]): string {
  return shells.join(',');
}

export type IonPrediction =
  | {
      kind: 'ion';
      symbol: ElementSymbol;
      charge: number;
      electronsMoved: number;
      shellsBefore: number[];
      shellsAfter: number[];
    }
  | { kind: 'no-simple-ion'; symbol: ElementSymbol; shellsBefore: number[]; reason: string }
  | { kind: 'unsupported'; symbol: string; reason: string };

/**
 * Applies the shell rule (lose 1–3 outer electrons or gain enough to reach 8) to a reviewed
 * element. Elements outside the reviewed list fail closed with a reason.
 */
export function predictIon(symbol: string): IonPrediction {
  const unsupported = ION_UNSUPPORTED_CASES.find((entry) => entry.symbol === symbol);
  if (unsupported) return { kind: 'unsupported', symbol, reason: unsupported.reason };
  const reviewed = ION_ELEMENT_CASES.find((entry) => entry.symbol === symbol);
  if (!reviewed) {
    return {
      kind: 'unsupported',
      symbol,
      reason: 'This element is not in the reviewed set, so the tool does not predict its ion.',
    };
  }
  const fact = ELEMENT_FACTS[reviewed.symbol];
  const shells = shellArrangement(fact.atomicNumber);
  if (!shells) return { kind: 'unsupported', symbol, reason: 'The shell model here covers Z = 1 to 20 only.' };
  const outer = shells[shells.length - 1];
  const reason = reviewed.expected.kind === 'no-simple-ion' ? reviewed.expected.reason : '';
  if (outer === FULL_OUTER_SHELL) {
    return { kind: 'no-simple-ion', symbol: reviewed.symbol, shellsBefore: shells, reason };
  }
  if (outer <= MAX_ELECTRONS_LOST) {
    return {
      kind: 'ion',
      symbol: reviewed.symbol,
      charge: outer,
      electronsMoved: outer,
      shellsBefore: shells,
      shellsAfter: shells.slice(0, -1),
    };
  }
  if (outer >= MIN_ELECTRONS_GAINED_VALENCE) {
    const gained = FULL_OUTER_SHELL - outer;
    return {
      kind: 'ion',
      symbol: reviewed.symbol,
      charge: -gained,
      electronsMoved: gained,
      shellsBefore: shells,
      shellsAfter: [...shells.slice(0, -1), FULL_OUTER_SHELL],
    };
  }
  return { kind: 'no-simple-ion', symbol: reviewed.symbol, shellsBefore: shells, reason };
}

export type IonChoice = 1 | 2 | 3 | -1 | -2 | -3 | 'none';

export const ION_CHOICES: IonChoice[] = [1, 2, 3, -1, -2, -3, 'none'];

export function ionChoiceLabel(choice: IonChoice): string {
  return choice === 'none' ? 'No simple ion' : `${signedCharge(choice)} ion`;
}

export interface IonCheckResult {
  correct: boolean;
  prediction: IonPrediction;
  feedback: string;
}

export function checkIonChoice(symbol: string, choice: IonChoice): IonCheckResult | null {
  const prediction = predictIon(symbol);
  if (prediction.kind === 'unsupported') return null;
  const name = ELEMENT_FACTS[prediction.symbol].name;
  const shells = formatShells(prediction.shellsBefore);
  const outer = prediction.shellsBefore[prediction.shellsBefore.length - 1];

  if (prediction.kind === 'no-simple-ion') {
    if (choice === 'none') {
      return { correct: true, prediction, feedback: `Correct. ${name} (${shells}): ${prediction.reason}` };
    }
    return { correct: false, prediction, feedback: `${name} (${shells}) does not form a simple ion. ${prediction.reason}` };
  }

  const { charge, electronsMoved } = prediction;
  const action = charge > 0 ? 'loses' : 'gains';
  const after = formatShells(prediction.shellsAfter);
  const ion = `${prediction.symbol}${formatCharge(charge)}`;
  if (choice === charge) {
    return {
      correct: true,
      prediction,
      feedback: `Correct. ${name} (${shells}) ${action} ${electronsMoved} electron${electronsMoved === 1 ? '' : 's'} to reach a full outer shell (${after}), forming ${ion}.`,
    };
  }
  if (choice === 'none') {
    return {
      correct: false,
      prediction,
      feedback: `${name} has ${outer} outer electron${outer === 1 ? '' : 's'}, not a full shell. It ${action} ${electronsMoved} to reach ${after}, so it forms ${ion}.`,
    };
  }
  if (Math.sign(choice) !== Math.sign(charge)) {
    const other = charge > 0 ? FULL_OUTER_SHELL - outer : outer;
    return {
      correct: false,
      prediction,
      feedback:
        charge > 0
          ? `Check the direction. ${name} has ${outer} outer electron${outer === 1 ? '' : 's'}: losing ${outer} is far easier than gaining ${other}. Losing negative electrons leaves a positive ion, ${ion}.`
          : `Check the direction. ${name} has ${outer} outer electrons: gaining ${electronsMoved} is far easier than losing ${other}. Gaining negative electrons gives a negative ion, ${ion}.`,
    };
  }
  return {
    correct: false,
    prediction,
    feedback: `Right sign, wrong size. ${name} (${shells}) ${action} ${electronsMoved} electron${electronsMoved === 1 ? '' : 's'}, not ${Math.abs(choice)}, to reach ${after}. The charge equals the number of electrons moved: ${ion}.`,
  };
}

/* ------------------------------------------------------ ionic formulas */

const MAX_IONS_PER_FORMULA = 4;
/** Multiplier used to build the unsimplified-ratio distractor. */
const NOT_SIMPLEST_FACTOR = 2;
export const ION_COUNT_RANGE = { min: 1, max: MAX_IONS_PER_FORMULA } as const;

export function ionById(id: string): IonSpecies | undefined {
  return ION_SPECIES.find((ion) => ion.id === id);
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

function ionGroup(ion: IonSpecies, count: number, brackets: boolean): string {
  if (count === 1) return ion.core;
  const body = ion.polyatomic && brackets ? `(${ion.core})` : ion.core;
  return `${body}${toSubscript(count)}`;
}

export function writeFormula(
  cation: IonSpecies,
  cationCount: number,
  anion: IonSpecies,
  anionCount: number,
  brackets = true,
): string {
  return `${ionGroup(cation, cationCount, brackets)}${ionGroup(anion, anionCount, brackets)}`;
}

export interface IonicFormula {
  cation: IonSpecies;
  anion: IonSpecies;
  cationCount: number;
  anionCount: number;
  formula: string;
}

/** Criss-cross with simplification. Null for unknown ions or a wrong charge sign. */
export function ionicFormula(cationId: string, anionId: string): IonicFormula | null {
  const cation = ionById(cationId);
  const anion = ionById(anionId);
  if (!cation || !anion || cation.charge <= 0 || anion.charge >= 0) return null;
  const positive = cation.charge;
  const negative = Math.abs(anion.charge);
  const divisor = greatestCommonDivisor(positive, negative);
  const cationCount = negative / divisor;
  const anionCount = positive / divisor;
  return { cation, anion, cationCount, anionCount, formula: writeFormula(cation, cationCount, anion, anionCount) };
}

export type CountStatus = 'correct' | 'unbalanced' | 'not-simplest' | 'invalid';

export interface CountCheckResult {
  status: CountStatus;
  positive: number;
  negative: number;
  feedback: string;
}

export function checkIonCounts(
  compound: IonicCompoundCase,
  cationCount: number,
  anionCount: number,
): CountCheckResult | null {
  const model = ionicFormula(compound.cationId, compound.anionId);
  if (!model) return null;
  const inRange = (value: number) =>
    Number.isInteger(value) && value >= ION_COUNT_RANGE.min && value <= ION_COUNT_RANGE.max;
  if (!inRange(cationCount) || !inRange(anionCount)) {
    return {
      status: 'invalid',
      positive: 0,
      negative: 0,
      feedback: `Use whole numbers from ${ION_COUNT_RANGE.min} to ${ION_COUNT_RANGE.max} for each ion.`,
    };
  }
  const cationText = ionLabel(model.cation);
  const anionText = ionLabel(model.anion);
  const positive = cationCount * model.cation.charge;
  const negative = anionCount * Math.abs(model.anion.charge);
  if (positive !== negative) {
    const short = positive < negative ? `positive: add ${cationText}` : `negative: add ${anionText}`;
    return {
      status: 'unbalanced',
      positive,
      negative,
      feedback: `Charges do not cancel: ${cationCount} × ${signedCharge(model.cation.charge)} = +${positive}, but ${anionCount} × ${signedCharge(model.anion.charge)} = −${negative}. The compound needs more ${short} (or remove the other) until the totals match.`,
    };
  }
  if (greatestCommonDivisor(cationCount, anionCount) > 1) {
    return {
      status: 'not-simplest',
      positive,
      negative,
      feedback: `The charges cancel (+${positive} and −${negative}), but ${cationCount} : ${anionCount} is not the simplest ratio. An ionic formula uses the lowest whole-number ratio: ${model.cationCount} : ${model.anionCount}.`,
    };
  }
  return {
    status: 'correct',
    positive,
    negative,
    feedback: `Correct. ${cationCount} × ${signedCharge(model.cation.charge)} and ${anionCount} × ${signedCharge(model.anion.charge)} cancel to zero, giving ${model.formula}.`,
  };
}

export type FormulaOptionKind = 'correct' | 'no-brackets' | 'one-to-one' | 'swapped' | 'not-simplest';

export interface FormulaOption {
  kind: FormulaOptionKind;
  formula: string;
}

const FORMULA_OPTION_FEEDBACK: Record<FormulaOptionKind, string> = {
  correct: 'Correct. The subscripts give the lowest whole-number ratio of ions, and brackets keep a polyatomic ion together.',
  'no-brackets':
    'The ratio is right, but without brackets the subscript only multiplies the last atom. Put the polyatomic ion in brackets before the subscript.',
  'one-to-one': 'A 1 : 1 formula leaves the charges unbalanced. Count the total positive and negative charge.',
  swapped: 'The numbers are on the wrong ions. Each ion’s count comes from the other ion’s charge (criss-cross).',
  'not-simplest': 'The charges balance, but an ionic formula shows the lowest whole-number ratio of ions. Divide both counts by their common factor.',
};

/** Distinct written formulas for the compound, sorted for a stable order. */
export function formulaOptions(compound: IonicCompoundCase): FormulaOption[] {
  const model = ionicFormula(compound.cationId, compound.anionId);
  if (!model) return [];
  const { cation, anion, cationCount, anionCount } = model;
  const candidates: FormulaOption[] = [
    { kind: 'correct', formula: model.formula },
    { kind: 'no-brackets', formula: writeFormula(cation, cationCount, anion, anionCount, false) },
    { kind: 'one-to-one', formula: writeFormula(cation, 1, anion, 1) },
    { kind: 'swapped', formula: writeFormula(cation, anionCount, anion, cationCount) },
    { kind: 'not-simplest', formula: writeFormula(cation, NOT_SIMPLEST_FACTOR * cationCount, anion, NOT_SIMPLEST_FACTOR * anionCount) },
  ];
  const seen = new Set<string>();
  return candidates
    .filter((option) => {
      if (seen.has(option.formula)) return false;
      seen.add(option.formula);
      return true;
    })
    .sort((a, b) => a.formula.localeCompare(b.formula));
}

export function formulaOptionFeedback(kind: FormulaOptionKind): string {
  return FORMULA_OPTION_FEEDBACK[kind];
}

/* ------------------------------------------------- covalent sharing */

export function outerShellTarget(symbol: ElementSymbol): number {
  return symbol === 'H' ? HYDROGEN_FULL_SHELL : FULL_OUTER_SHELL;
}

export function electronsNeeded(symbol: ElementSymbol): number {
  return outerShellTarget(symbol) - ELEMENT_FACTS[symbol].valenceElectrons;
}

export function bondsOf(structure: MolecularStructure, index: number) {
  return structure.bonds.filter((bond) => bond.from === index || bond.to === index);
}

/** Electrons around an atom in the drawn structure: lone pairs plus every shared pair it is in. */
export function electronsAround(structure: MolecularStructure, index: number): number {
  const shared = bondsOf(structure, index).reduce((sum, bond) => sum + bond.order, 0);
  return 2 * structure.atoms[index].lonePairs + 2 * shared;
}

export function sharedPairCount(structure: MolecularStructure): number {
  return structure.bonds.reduce((sum, bond) => sum + bond.order, 0);
}

export function lonePairCount(structure: MolecularStructure): number {
  return structure.atoms.reduce((sum, item) => sum + item.lonePairs, 0);
}

/** Sum of outer electrons minus the species charge. */
export function totalValenceElectrons(structure: MolecularStructure): number {
  return structure.atoms.reduce((sum, item) => sum + ELEMENT_FACTS[item.element].valenceElectrons, 0) - structure.charge;
}

/** Shared pairs predicted from how many electrons each atom needs (valid for these simple cases). */
export function sharedPairsFromNeeds(molecule: CovalentMoleculeCase): number {
  const needed = molecule.structure.atoms.reduce((sum, item) => sum + electronsNeeded(item.element), 0);
  return needed / 2;
}

export const SHARED_PAIR_CHOICES = [1, 2, 3, 4] as const;

export interface SharedPairResult {
  correct: boolean;
  expected: number;
  feedback: string;
}

export function checkSharedPairs(moleculeId: string, choice: number): SharedPairResult | null {
  const molecule = COVALENT_MOLECULE_CASES.find((entry) => entry.id === moleculeId);
  if (!molecule) return null;
  const expected = sharedPairCount(molecule.structure);
  const needs = molecule.structure.atoms
    .map((item) => `${item.element} needs ${electronsNeeded(item.element)}`)
    .join(', ');
  const needTotal = molecule.structure.atoms.reduce((sum, item) => sum + electronsNeeded(item.element), 0);
  const working = `${needs}: ${needTotal} electrons needed in total. Each shared pair gives one extra electron to two atoms, so ${needTotal} ÷ 2 = ${expected} shared pair${expected === 1 ? '' : 's'}.`;
  if (choice === expected) {
    return { correct: true, expected, feedback: `Correct. ${working} ${molecule.note}` };
  }
  const direction = choice < expected ? 'Too few' : 'Too many';
  return {
    correct: false,
    expected,
    feedback: `${direction} pairs for ${molecule.formula}. ${working}`,
  };
}

/* ------------------------------------------------ diagram geometry */

const FULL_TURN = 360;

function normaliseAngle(angle: number): number {
  return ((angle % FULL_TURN) + FULL_TURN) % FULL_TURN;
}

/** Direction (degrees, y downwards) from atom `a` to atom `b`. */
export function directionBetween(structure: MolecularStructure, a: number, b: number): number {
  const first = structure.atoms[a];
  const second = structure.atoms[b];
  return normaliseAngle((Math.atan2(second.y - first.y, second.x - first.x) * 180) / Math.PI);
}

/**
 * Places an atom's lone pairs in the largest empty gaps between its bonds. A gap that receives
 * several pairs shares them evenly, so the drawing stays balanced.
 */
export function lonePairAngles(structure: MolecularStructure, index: number): number[] {
  const count = structure.atoms[index].lonePairs;
  if (count === 0) return [];
  const bondAngles = bondsOf(structure, index)
    .map((bond) => directionBetween(structure, index, bond.from === index ? bond.to : bond.from))
    .sort((a, b) => a - b);
  if (bondAngles.length === 0) {
    return Array.from({ length: count }, (_, k) => (FULL_TURN * k) / count);
  }
  const gaps = bondAngles.map((start, i) => {
    const next = i === bondAngles.length - 1 ? bondAngles[0] + FULL_TURN : bondAngles[i + 1];
    return { start, size: next - start, assigned: 0 };
  });
  for (let placed = 0; placed < count; placed += 1) {
    const best = gaps.reduce((winner, gap) => {
      const score = gap.size / (gap.assigned + 1);
      const winnerScore = winner.size / (winner.assigned + 1);
      if (score > winnerScore + 1e-9) return gap;
      if (Math.abs(score - winnerScore) <= 1e-9 && gap.size > winner.size) return gap;
      return winner;
    });
    best.assigned += 1;
  }
  return gaps.flatMap((gap) =>
    Array.from({ length: gap.assigned }, (_, k) => normaliseAngle(gap.start + (gap.size * (k + 1)) / (gap.assigned + 1))),
  );
}

/* ------------------------------------------------ structure types */

/** At or above this melting point (°C) a non-conducting solid is treated as a covalent network. */
export const NETWORK_MELTING_THRESHOLD_C = 1000;
/** At or above this melting point (°C) the learner should call the melting point "high". */
export const HIGH_MELTING_THRESHOLD_C = 300;

export interface PropertyEvidence {
  meltingPointC: number | null;
  conductsSolid: boolean;
  conductsMolten: boolean | null;
}

/**
 * Classifies structure from evidence. Returns null for an inconsistent property set, such as a
 * substance that conducts as a solid but not when molten.
 */
export function classifyStructure(evidence: PropertyEvidence): StructureType | null {
  const { meltingPointC, conductsSolid, conductsMolten } = evidence;
  if (conductsSolid) {
    if (conductsMolten === true) return 'metallic';
    if (conductsMolten === null) return 'covalent-network';
    return null;
  }
  if (conductsMolten === true) return 'ionic';
  if (meltingPointC === null || meltingPointC >= NETWORK_MELTING_THRESHOLD_C) return 'covalent-network';
  return 'simple-molecular';
}

export function formatMeltingPoint(substance: StructureSubstance): string {
  if (substance.meltingPointC === null) return substance.meltingLabel ?? 'Does not melt at 100 kPa';
  const value = substance.meltingPointC;
  return `${value < 0 ? '−' : ''}${Math.abs(value)} °C`;
}

function mismatchReason(substance: StructureSubstance, chosen: StructureType): string {
  const melting = formatMeltingPoint(substance);
  switch (chosen) {
    case 'ionic':
      if (substance.conductsSolid) return 'Ionic solids do not conduct: their ions are locked in the lattice. This substance conducts as a solid.';
      return 'An ionic compound conducts when molten because its ions become free to move. This substance does not.';
    case 'simple-molecular':
      if (substance.conductsSolid || substance.conductsMolten) {
        return 'Simple molecular substances have no free ions or electrons, so they do not conduct. This substance does.';
      }
      return `Simple molecular substances melt at low temperatures because only weak forces between molecules break. This one: ${melting}.`;
    case 'covalent-network':
      if (substance.conductsMolten) {
        return 'A covalent network does not melt into free charges, so it would not conduct as a liquid. This substance does.';
      }
      return `A covalent network needs many strong covalent bonds broken, so its melting point is very high. This one: ${melting}.`;
    case 'metallic':
      if (!substance.conductsSolid) {
        return 'Metals conduct as solids because of delocalised electrons. This substance does not conduct as a solid.';
      }
      return 'A metal melts and still conducts as a liquid. This substance does not melt at 100 kPa, so its conduction comes from a different structure.';
    default:
      return '';
  }
}

export interface StructureChoiceResult {
  correct: boolean;
  expected: StructureType;
  feedback: string;
}

export function checkStructureChoice(substance: StructureSubstance, chosen: StructureType): StructureChoiceResult | null {
  const expected = classifyStructure(substance);
  if (!expected || expected !== substance.structure) return null;
  const exception = substance.exception ? ` ${substance.exception}` : '';
  if (chosen === expected) {
    return {
      correct: true,
      expected,
      feedback: `Correct — ${STRUCTURE_LABELS[expected].toLowerCase()}. ${substance.explanation}${exception}`,
    };
  }
  return {
    correct: false,
    expected,
    feedback: `${mismatchReason(substance, chosen)} The evidence points to ${STRUCTURE_LABELS[expected].toLowerCase()}: ${substance.explanation}${exception}`,
  };
}

export type MeltingBand = 'high' | 'low';

export interface PropertyPrediction {
  meltingBand: MeltingBand;
  conductsSolid: boolean;
  conductsMolten: boolean;
}

export function expectedProperties(substance: StructureSubstance): PropertyPrediction {
  return {
    meltingBand:
      substance.meltingPointC === null || substance.meltingPointC >= HIGH_MELTING_THRESHOLD_C ? 'high' : 'low',
    conductsSolid: substance.conductsSolid,
    conductsMolten: substance.conductsMolten === true,
  };
}

export interface PropertyFieldResult {
  field: keyof PropertyPrediction;
  correct: boolean;
  feedback: string;
}

const yesNo = (value: boolean) => (value ? 'yes' : 'no');

export function checkPropertyPrediction(
  substance: StructureSubstance,
  prediction: PropertyPrediction,
): PropertyFieldResult[] {
  const expected = expectedProperties(substance);
  const type = STRUCTURE_LABELS[substance.structure].toLowerCase();
  const melting = formatMeltingPoint(substance);
  const exception = substance.exception ? ` ${substance.exception}` : '';
  const meltingCorrect = prediction.meltingBand === expected.meltingBand;
  const solidCorrect = prediction.conductsSolid === expected.conductsSolid;
  const moltenCorrect = prediction.conductsMolten === expected.conductsMolten;
  return [
    {
      field: 'meltingBand',
      correct: meltingCorrect,
      feedback: meltingCorrect
        ? `Melting point ${melting} — ${expected.meltingBand}, as expected.`
        : `Melting point is ${melting}, which is ${expected.meltingBand} (the cut-off here is ${HIGH_MELTING_THRESHOLD_C} °C).${exception}`,
    },
    {
      field: 'conductsSolid',
      correct: solidCorrect,
      feedback: solidCorrect
        ? `Conducts as a solid: ${yesNo(expected.conductsSolid)}.`
        : expected.conductsSolid
          ? `It does conduct as a solid: this ${type} substance has mobile electrons.${exception}`
          : `It does not conduct as a solid: a ${type} solid has no charged particles free to move.`,
    },
    {
      field: 'conductsMolten',
      correct: moltenCorrect,
      feedback: moltenCorrect
        ? `Conducts when molten: ${yesNo(expected.conductsMolten)}${substance.conductsMolten === null ? ' (it does not melt at 100 kPa)' : ''}.`
        : substance.conductsMolten === null
          ? 'It does not melt at 100 kPa, so it cannot conduct as a liquid.'
          : expected.conductsMolten
            ? `It does conduct when molten: ${substance.structure === 'ionic' ? 'its ions become free to move' : 'delocalised electrons still move'}.`
            : 'It does not conduct when molten: melting frees whole molecules or atoms, not charged particles.',
    },
  ];
}

/* ------------------------------------------------------------- VSEPR */

/** Bonds with an electronegativity difference at or above this are treated as polar. */
export const POLAR_BOND_THRESHOLD = 0.4;

const SHAPE_TABLE: { domains: number; lonePairs: number; shape: ShapeName; idealAngle: string }[] = [
  { domains: 2, lonePairs: 0, shape: 'linear', idealAngle: '180°' },
  { domains: 3, lonePairs: 0, shape: 'trigonal planar', idealAngle: '120°' },
  { domains: 3, lonePairs: 1, shape: 'bent', idealAngle: 'just under 120°' },
  { domains: 4, lonePairs: 0, shape: 'tetrahedral', idealAngle: '109.5°' },
  { domains: 4, lonePairs: 1, shape: 'trigonal pyramidal', idealAngle: 'just under 109.5°' },
  { domains: 4, lonePairs: 2, shape: 'bent', idealAngle: 'well under 109.5°' },
  { domains: 5, lonePairs: 0, shape: 'trigonal bipyramidal', idealAngle: '90° and 120°' },
];

/** Shape for a central atom's domain count. Null for combinations this tool does not support. */
export function shapeFor(domains: number, lonePairs: number): ShapeName | null {
  return SHAPE_TABLE.find((row) => row.domains === domains && row.lonePairs === lonePairs)?.shape ?? null;
}

export function idealAngleFor(domains: number, lonePairs: number): string | null {
  return SHAPE_TABLE.find((row) => row.domains === domains && row.lonePairs === lonePairs)?.idealAngle ?? null;
}

export function electronDomains(structure: MolecularStructure, index: number): number {
  return bondsOf(structure, index).length + structure.atoms[index].lonePairs;
}

export function electronegativityDifference(a: ElementSymbol, b: ElementSymbol): number | null {
  const first = ELEMENT_FACTS[a].electronegativity;
  const second = ELEMENT_FACTS[b].electronegativity;
  if (first === null || second === null) return null;
  return Math.round(Math.abs(first - second) * 100) / 100;
}

export interface BondPolarityStep {
  pair: string;
  difference: number;
  polar: boolean;
  /** For example "|χO − χH| = |3.44 − 2.20| = 1.24 → polar covalent bond (≥ 0.4)". */
  working: string;
}

/**
 * Worked bond-polarity step from the reviewed Pauling values (CT-INTERACT worked step). The more
 * electronegative atom is written first. Null when either element has no value in this tool.
 */
export function bondPolarityWorking(a: ElementSymbol, b: ElementSymbol): BondPolarityStep | null {
  const difference = electronegativityDifference(a, b);
  const chiA = ELEMENT_FACTS[a].electronegativity;
  const chiB = ELEMENT_FACTS[b].electronegativity;
  if (difference === null || chiA === null || chiB === null) return null;
  const [high, low] = chiA >= chiB ? [{ symbol: a, chi: chiA }, { symbol: b, chi: chiB }] : [{ symbol: b, chi: chiB }, { symbol: a, chi: chiA }];
  const polar = difference >= POLAR_BOND_THRESHOLD;
  const verdict = polar ? `polar covalent bond (≥ ${POLAR_BOND_THRESHOLD})` : `non-polar covalent bond (< ${POLAR_BOND_THRESHOLD})`;
  return {
    pair: `${high.symbol}–${low.symbol}`,
    difference,
    polar,
    working: `|χ${high.symbol} − χ${low.symbol}| = |${high.chi.toFixed(2)} − ${low.chi.toFixed(2)}| = ${difference.toFixed(2)} → ${verdict}`,
  };
}

/** One worked step per distinct bonded element pair in a structure; null if any pair has no value. */
export function bondPolaritySteps(structure: MolecularStructure): BondPolarityStep[] | null {
  const steps: BondPolarityStep[] = [];
  for (const bond of structure.bonds) {
    const step = bondPolarityWorking(structure.atoms[bond.from].element, structure.atoms[bond.to].element);
    if (!step) return null;
    if (!steps.some((existing) => existing.pair === step.pair)) steps.push(step);
  }
  return steps;
}

export type Polarity = 'polar' | 'non-polar' | 'ion';

export const POLARITY_LABELS: Record<Polarity, string> = {
  polar: 'Polar molecule',
  'non-polar': 'Non-polar molecule',
  ion: 'Ion (charged, not classed as polar or non-polar)',
};

export interface PolarityAnalysis {
  polarity: Polarity;
  polarBonds: { pair: string; difference: number }[];
  symmetric: boolean;
  reason: string;
}

export function analysePolarity(structure: MolecularStructure): PolarityAnalysis | null {
  const polarBonds: { pair: string; difference: number }[] = [];
  for (const bond of structure.bonds) {
    const a = structure.atoms[bond.from].element;
    const b = structure.atoms[bond.to].element;
    const difference = electronegativityDifference(a, b);
    if (difference === null) return null;
    if (difference >= POLAR_BOND_THRESHOLD && !polarBonds.some((entry) => entry.pair === `${a}–${b}`)) {
      polarBonds.push({ pair: `${a}–${b}`, difference });
    }
  }
  const neighbourCounts = structure.atoms.map((_, index) => bondsOf(structure, index).length);
  const centres = structure.atoms.filter((_, index) => neighbourCounts[index] > 1);
  const terminals = structure.atoms.filter((_, index) => neighbourCounts[index] === 1);
  const symmetric =
    centres.every((centre) => centre.lonePairs === 0) &&
    terminals.every((terminal) => terminal.element === terminals[0]?.element);

  if (structure.charge !== 0) {
    return {
      polarity: 'ion',
      polarBonds,
      symmetric,
      reason: 'This is an ion. Its overall charge dominates, so it is described by its charge, not as a polar or non-polar molecule.',
    };
  }
  if (polarBonds.length === 0) {
    return {
      polarity: 'non-polar',
      polarBonds,
      symmetric,
      reason: `No bond has an electronegativity difference of ${POLAR_BOND_THRESHOLD} or more, so there are no significant bond dipoles.`,
    };
  }
  const bondText = polarBonds.map((entry) => `${entry.pair} (Δ = ${entry.difference.toFixed(2)})`).join(', ');
  if (symmetric) {
    return {
      polarity: 'non-polar',
      polarBonds,
      symmetric,
      reason: `${bondText} ${polarBonds.length === 1 ? 'is' : 'are'} polar, but identical bonds arranged symmetrically with no lone pair on the centre cancel out.`,
    };
  }
  return {
    polarity: 'polar',
    polarBonds,
    symmetric,
    reason: `${bondText} ${polarBonds.length === 1 ? 'is' : 'are'} polar, and the shape is not symmetrical (a lone pair on the centre or different outer atoms), so the dipoles do not cancel.`,
  };
}

export interface VseprAnalysis {
  valenceElectrons: number;
  domains: number;
  lonePairs: number;
  shape: ShapeName;
  idealAngle: string;
  polarity: PolarityAnalysis;
}

export function analyseVsepr(species: VseprSpeciesCase): VseprAnalysis | null {
  const { structure, centralIndex } = species;
  if (!structure.atoms[centralIndex]) return null;
  const domains = electronDomains(structure, centralIndex);
  const lonePairs = structure.atoms[centralIndex].lonePairs;
  const shape = shapeFor(domains, lonePairs);
  const idealAngle = idealAngleFor(domains, lonePairs);
  const polarity = analysePolarity(structure);
  if (!shape || !idealAngle || !polarity) return null;
  return { valenceElectrons: totalValenceElectrons(structure), domains, lonePairs, shape, idealAngle, polarity };
}

export interface VseprAnswers {
  valenceElectrons: number | null;
  domains: number | null;
  lonePairs: number | null;
  shape: ShapeName | null;
  polarity: Polarity | null;
}

export interface VseprFieldResult {
  field: keyof VseprAnswers;
  correct: boolean;
  feedback: string;
}

export function checkVsepr(species: VseprSpeciesCase, answers: VseprAnswers): VseprFieldResult[] | null {
  const model = analyseVsepr(species);
  if (!model) return null;
  const central = species.structure.atoms[species.centralIndex].element;
  const bonded = bondsOf(species.structure, species.centralIndex).length;
  const valenceParts = species.structure.atoms.map((item) => ELEMENT_FACTS[item.element].valenceElectrons).join(' + ');
  const chargeText =
    species.structure.charge === 0 ? '' : ` ${species.structure.charge > 0 ? '−' : '+'} ${Math.abs(species.structure.charge)} for the ${species.structure.charge > 0 ? 'positive' : 'negative'} charge`;
  const multipleBond = bondsOf(species.structure, species.centralIndex).some((bond) => bond.order > 1);

  return [
    {
      field: 'valenceElectrons',
      correct: answers.valenceElectrons === model.valenceElectrons,
      feedback:
        answers.valenceElectrons === model.valenceElectrons
          ? `${model.valenceElectrons} valence electrons (${model.valenceElectrons / 2} pairs).`
          : `Add every atom’s outer electrons: ${valenceParts}${chargeText} = ${model.valenceElectrons}.`,
    },
    {
      field: 'domains',
      correct: answers.domains === model.domains,
      feedback:
        answers.domains === model.domains
          ? `${model.domains} electron domains around ${central}.`
          : `${central} has ${bonded} bonded atom${bonded === 1 ? '' : 's'} and ${model.lonePairs} lone pair${model.lonePairs === 1 ? '' : 's'}: ${model.domains} domains.${multipleBond ? ' A double or triple bond counts as one domain.' : ''}`,
    },
    {
      field: 'lonePairs',
      correct: answers.lonePairs === model.lonePairs,
      feedback:
        answers.lonePairs === model.lonePairs
          ? `${model.lonePairs} lone pair${model.lonePairs === 1 ? '' : 's'} on ${central}.`
          : `After bonding, ${central} keeps ${model.lonePairs} non-bonding pair${model.lonePairs === 1 ? '' : 's'}. Count only pairs on the central atom.`,
    },
    {
      field: 'shape',
      correct: answers.shape === model.shape,
      feedback:
        answers.shape === model.shape
          ? `${model.shape} — angle ${species.angleLabel}.`
          : `${model.domains} domains with ${model.lonePairs} lone pair${model.lonePairs === 1 ? '' : 's'} gives ${model.shape}. Shape names describe atom positions only; lone pairs push the bonds but are not part of the name.`,
    },
    {
      field: 'polarity',
      correct: answers.polarity === model.polarity.polarity,
      feedback: `${POLARITY_LABELS[model.polarity.polarity]}. ${model.polarity.reason}`,
    },
  ];
}

/** 2D projection used to sketch each VSEPR shape (wedge = towards viewer, dash = away). */
export type SketchBondStyle = 'plane' | 'wedge' | 'dash';

export interface ShapeSketch {
  bonds: { angle: number; style: SketchBondStyle }[];
  lonePairs: number[];
}

const BENT_CENTRE_ANGLE = 90;
const HALF = 2;

export function shapeSketch(shape: ShapeName, lonePairs: number, angleDegrees: number): ShapeSketch {
  switch (shape) {
    case 'linear':
      return { bonds: [{ angle: 180, style: 'plane' }, { angle: 0, style: 'plane' }], lonePairs: [] };
    case 'trigonal planar':
      return {
        bonds: [{ angle: 270, style: 'plane' }, { angle: 150, style: 'plane' }, { angle: 30, style: 'plane' }],
        lonePairs: [],
      };
    case 'bent':
      return {
        bonds: [
          { angle: BENT_CENTRE_ANGLE + angleDegrees / HALF, style: 'plane' },
          { angle: BENT_CENTRE_ANGLE - angleDegrees / HALF, style: 'plane' },
        ],
        lonePairs: lonePairs === 1 ? [270] : [235, 305],
      };
    case 'tetrahedral':
      return {
        bonds: [
          { angle: 270, style: 'plane' },
          { angle: 155, style: 'plane' },
          { angle: 25, style: 'wedge' },
          { angle: 65, style: 'dash' },
        ],
        lonePairs: [],
      };
    case 'trigonal pyramidal':
      return {
        bonds: [{ angle: 150, style: 'plane' }, { angle: 30, style: 'wedge' }, { angle: 80, style: 'dash' }],
        lonePairs: [270],
      };
    case 'trigonal bipyramidal':
      return {
        bonds: [
          { angle: 270, style: 'plane' },
          { angle: 90, style: 'plane' },
          { angle: 180, style: 'plane' },
          { angle: 20, style: 'wedge' },
          { angle: 340, style: 'dash' },
        ],
        lonePairs: [],
      };
    default:
      return { bonds: [], lonePairs: [] };
  }
}
